import moment from 'moment'
import BadgeText from 'components/BadgeText'
import PersonName from 'components/PersonName'
import Table, { TRowProp } from 'components/Table'
import { useAppSelector } from 'redux/hooks'
import RecordingPlayer from './RecordingPlayer'
import {
  AI_CALL_STATUS_MAP,
  getPersonAvatar,
  getPersonDisplayName,
} from 'pages/AiCalls/helpers/constant'

export type TAiCallsTableProp = {
  loading: boolean
  handleFormChange: (key: string, value: any) => void
  clickedRow: (row: TRowProp) => void
}

const formatDate = (value?: string) => {
  if (!value) {
    return '-'
  }
  return moment(value).format('MMM DD, YYYY h:mm A')
}

const formatPhone = (value?: string) => {
  if (!value) {
    return '-'
  }

  const digits = value.replace(/\D/g, '')
  const phone = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits

  if (phone.length !== 10) {
    return value
  }

  return `(${phone.slice(0, 3)})${phone.slice(3, 6)}-${phone.slice(6)}`
}

export default function AiCallsTable({
  loading,
  handleFormChange,
  clickedRow,
}: TAiCallsTableProp): JSX.Element {
  const { page, total, pageSize, data } = useAppSelector(
    state => state.aiCalls,
  )
  const { sort_by, sort } = useAppSelector(state => state.aiCalls.sortData)

  const handleSort = (type: string, value: string) => {
    handleFormChange('sort', {
      type,
      value,
    })
  }

  const columns = [
    {
      key: 'status',
      name: 'STATUS',
      className: 'w-[150px]',
      thClassName: 'text-xs text-light-300 px-4 py-2 whitespace-nowrap',
      sortable: true,
      sortDirection: sort_by === 'status' ? sort : '',
      onSortDirection: (key: string, item: { label: string; value: string }) =>
        handleSort('status', item.value),
      custom: (value: string) => {
        const status = AI_CALL_STATUS_MAP[value]
        return (
          <BadgeText
            className='whitespace-nowrap'
            value={status?.text || value || '-'}
            color={status?.color || 'default'}
          />
        )
      },
    },
    {
      key: 'brand_name',
      name: 'BRAND NAME',
      custom: (value: string) => <div className='text-ellipsis'>{value || '-'}</div>,
    },
    {
      key: 'caller_name',
      name: 'CALLER NAME',
      custom: (value: string) => <div className='text-ellipsis'>{value || '-'}</div>,
    },
    {
      key: 'caller_phone',
      name: 'CALLER PHONE',
      className: 'w-[140px]',
      thClassName: 'text-xs text-light-300 px-4 py-2 whitespace-nowrap',
      custom: (value: string) => <div>{formatPhone(value)}</div>,
    },
    {
      key: 'callback_number',
      name: 'CALLBACK',
      className: 'w-[132px]',
      thClassName: 'text-xs text-light-300 px-4 py-2 whitespace-nowrap',
      custom: (value: string) => <div>{formatPhone(value)}</div>,
    },
    {
      key: 'issue_category',
      name: 'ISSUE CATEGORY',
      className: 'w-[152px]',
      thClassName: 'text-xs text-light-300 px-4 py-2 whitespace-nowrap',
      custom: (value: string) => <div className='text-ellipsis'>{value || '-'}</div>,
    },
    {
      key: 'priority',
      name: 'PRIORITY',
      className: 'w-[128px]',
      thClassName: 'text-xs text-light-300 px-4 py-2 whitespace-nowrap',
      sortable: true,
      sortDirection: sort_by === 'priority' ? sort : '',
      onSortDirection: (key: string, item: { label: string; value: string }) =>
        handleSort('priority', item.value),
      custom: (value: string) =>
        value ? <BadgeText value={value} color='blue' /> : <div>-</div>,
    },
    {
      key: 'assignee',
      name: 'ASSIGNEE',
      tdNoHidden: true,
      custom: (value: any) => (
        <div className='flex h-10 items-center'>
          <PersonName
            className='w-full'
            avatar={getPersonAvatar(value)}
            name={getPersonDisplayName(value)}
          />
        </div>
      ),
    },
    {
      key: 'preferred_follow_up_method',
      name: 'PREFERRED',
      className: 'w-[128px]',
      thClassName: 'text-xs text-light-300 px-4 py-2 whitespace-nowrap',
      custom: (value: string) => <div className='text-ellipsis'>{value || '-'}</div>,
    },
    {
      key: 'recording_url',
      name: 'RECORDING',
      className: 'w-[300px]',
      thClassName: 'text-xs text-light-300 px-4 py-2 whitespace-nowrap',
      tdNoHidden: true,
      custom: (value: string) => <RecordingPlayer url={value} compact />,
    },
    {
      key: 'created_at',
      name: 'CREATED',
      className: 'w-[196px]',
      thClassName: 'text-xs text-light-300 px-4 py-2 whitespace-nowrap',
      tdNoHidden: true,
      sortable: true,
      sortDirection: sort_by === 'created_at' ? sort : '',
      onSortDirection: (key: string, item: { label: string; value: string }) =>
        handleSort('created_at', item.value),
      custom: (value: string) => <div>{formatDate(value)}</div>,
    },
    {
      key: 'updated_at',
      name: 'UPDATED',
      className: 'w-[196px]',
      thClassName: 'text-xs text-light-300 px-4 py-2 whitespace-nowrap',
      tdNoHidden: true,
      sortable: true,
      sortDirection: sort_by === 'updated_at' ? sort : '',
      onSortDirection: (key: string, item: { label: string; value: string }) =>
        handleSort('updated_at', item.value),
      custom: (value: string) => <div>{formatDate(value)}</div>,
    },
  ]

  return (
    <Table
      sticky
      loading={loading}
      columns={columns}
      data={data}
      paginationShow
      total={total}
      current={page}
      resizePageSize={pageSize}
      containerClassName='overflow-x-auto'
      className='w-full min-w-[1860px]'
      onPageNoChange={value => handleFormChange?.('page', value)}
      clickedRow={clickedRow}
    />
  )
}
